import { useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { locationContent } from '../../mocks/content';

export default function LocationPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white font-['Montserrat']">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm tracking-[0.2em] uppercase text-[#5E7A92] mb-6">
            LOCATION
          </p>
          <h1 className="text-5xl md:text-6xl font-bold uppercase mb-8 leading-tight tracking-tight">
            Wanstead — Your Village Sanctuary
          </h1>
          <p className="text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto">
            RE:SET is rooted in Wanstead — a calm, connected village in East London. 
            This is where community meets sanctuary, where local life and longevity practice intersect.
          </p>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 px-6 bg-[#E5E8EF]">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg overflow-hidden shadow-sm">
            <iframe
              src={locationContent.mapEmbed}
              width="100%"
              height="500"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="RE:SET Location Map"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Address & Transport Details */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16">
          {/* Address */}
          <div>
            <h2 className="text-3xl font-bold uppercase mb-8 tracking-tight">
              Find Us
            </h2>
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
              <p className="font-semibold text-black">{locationContent.address}</p>
              <p>{locationContent.street}</p>
              <p>{locationContent.city}</p>
            </div>
            <div className="mt-12 pt-12 border-t border-gray-200">
              <h3 className="text-xl font-bold uppercase mb-4 tracking-tight">
                Transport
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                <i className="ri-train-line text-[#5E7A92] mr-3"></i>
                {locationContent.transport}
              </p>
              <p className="text-base text-gray-600 mt-4 leading-relaxed">
                Easily accessible from Central London via the Central Line. 
                Free parking available on surrounding streets.
              </p>
            </div>
          </div>

          {/* Why Wanstead */}
          <div>
            <h2 className="text-3xl font-bold uppercase mb-8 tracking-tight">
              Why Wanstead
            </h2>
            <div className="space-y-6 text-base text-gray-700 leading-relaxed">
              <p>
                Wanstead is a village within the city — calm, green, and quietly confident. 
                It's a place where people know each other, where independent businesses thrive, 
                and where community matters.
              </p>
              <p>
                RE:SET belongs here because wellness isn't something you travel across the city for 
                once a month. It's something you return to, again and again. A ritual woven into 
                your weekly rhythm.
              </p>
              <p>
                We're five minutes from Wanstead Station, surrounded by tree-lined streets, 
                independent cafés, and Wanstead Flats — one of London's largest open spaces. 
                This is where urban life meets village calm.
              </p>
              <p className="font-semibold text-black pt-6">
                A sanctuary you can walk to. A practice you can sustain. A community you belong to.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Neighborhood Context */}
      <section className="py-24 px-6 bg-[#E5E8EF]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold uppercase mb-12 text-center tracking-tight">
            The Wanstead Experience
          </h2>
          <div className="grid md:grid-cols-3 gap-12">
            {/* Village Life */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <i className="ri-community-line text-5xl text-[#5E7A92]"></i>
              </div>
              <h3 className="text-xl font-bold uppercase mb-4 tracking-tight">
                Village Life
              </h3>
              <p className="text-base text-gray-700 leading-relaxed">
                A close-knit community where independent businesses, local families, 
                and wellness-minded individuals create a unique village atmosphere within London.
              </p>
            </div>

            {/* Green Space */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <i className="ri-leaf-line text-5xl text-[#5E7A92]"></i>
              </div>
              <h3 className="text-xl font-bold uppercase mb-4 tracking-tight">
                Green Space
              </h3>
              <p className="text-base text-gray-700 leading-relaxed">
                Wanstead Flats, one of London's largest open spaces, is moments away. 
                Tree-lined streets and calm residential areas create a sanctuary feel.
              </p>
            </div>

            {/* Connected Living */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <i className="ri-map-pin-line text-5xl text-[#5E7A92]"></i>
              </div>
              <h3 className="text-xl font-bold uppercase mb-4 tracking-tight">
                Connected Living
              </h3>
              <p className="text-base text-gray-700 leading-relaxed">
                Five minutes from Wanstead Station on the Central Line. Easy access from 
                Central London, Stratford, and surrounding East London neighborhoods.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Local Area Image */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold uppercase mb-8 tracking-tight">
                A Place To Return To
              </h2>
              <p className="text-base text-gray-700 leading-relaxed mb-6">
                RE:SET is designed for recurring rituals, not one-off visits. We're here for 
                your weekly reformer class, your Friday thermal session, your morning coffee 
                before work.
              </p>
              <p className="text-base text-gray-700 leading-relaxed mb-6">
                This is wellness built into your life, not added on top of it. A sanctuary 
                you can walk to. A practice you can sustain.
              </p>
              <p className="text-base text-gray-700 leading-relaxed font-semibold text-black">
                Because true wellbeing isn't about grand gestures — it's about showing up, 
                again and again, in a place that feels like home.
              </p>
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/ed66a055830cb1055271d3021fe7730e.jpg"
                alt="Wanstead Village Street"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-[#5E7A92] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold uppercase mb-8 tracking-tight">
            Visit RE:SET
          </h2>
          <p className="text-lg mb-12 leading-relaxed opacity-90">
            Come see the space. Meet the team. Experience the calm. 
            Book a class, reserve a thermal session, or simply stop by the café.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="/memberships"
              className="inline-block px-10 py-4 bg-white text-[#5E7A92] rounded-full font-semibold uppercase tracking-wide text-sm hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Explore Memberships
            </a>
            <a
              href="/contact"
              className="inline-block px-10 py-4 bg-transparent border-2 border-white text-white rounded-full font-semibold uppercase tracking-wide text-sm hover:bg-white hover:text-[#5E7A92] transition-colors whitespace-nowrap cursor-pointer"
            >
              Get In Touch
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
